import {
  responseAnswerGapClass,
  responseColumnClass,
  responseTurnClass,
  useProseDensity,
} from '@heidi/answer-kit';
import { cn } from '@heidi/utils';
import type { ReactNode, RefObject } from 'react';
import type { ChatItem } from '../../../contracts/harness.types';
import { isVisibleUserMessage } from '../../../data/projects/components/projectChatMessage.helpers';
import { groupProjectTurns } from '../../../data/projects/lib/turnLifecycle';
import {
  AgentTimeline,
  type AgentTimelineProps,
} from '../../agent-harness/components/agent-timeline/agent-timeline';
import { RESPONSE_ACTIONS_HOVER_GROUP_CLASS_NAME } from './ResponseActionsReveal';

interface ProjectTurnTimelineProps {
  items: ChatItem[];
  latestTurnMinHeight?: number | null;
  renderItem: NonNullable<AgentTimelineProps['renderItem']>;
  tailRef: RefObject<HTMLDivElement>;
  tailContent?: ReactNode;
  /** Rendered after each completed (not latest) turn — its response action row.
   *  The latest turn's row rides `tailContent` instead. */
  renderCompletedTurnFooter?: (turn: CompletedTurn) => ReactNode;
}

interface CompletedTurn {
  items: ChatItem[];
  /** First item of the turn that followed, for resolving transcript position. */
  nextTurnFirstItem: ChatItem | undefined;
}
/**
 * Groups the harness timeline into user-initiated turns and owns the
 * latest-turn room/sentinel contract used by Project chat scrolling.
 *
 * Latest turn layout (AGE-633 + first-send remount):
 * - An unkeyed ``project-chat-latest-turn`` wrapper owns ``latestTurnMinHeight``
 *   so the snap floor still begins at the question (not below the reply).
 * - Inside it, the bubble/reply ``AgentTimeline`` is keyed by ``turnId``
 *   when the group has one, otherwise the opening message id.
 * - ``tailContent`` (thinking) + follow-tail live in a sibling ``turn-tail``
 *   region with no React key — so the indicator stays mounted across that
 *   id swap without moving the floor off the question.
 */
export const ProjectTurnTimeline = ({
  items,
  latestTurnMinHeight,
  renderItem,
  tailRef,
  tailContent,
  renderCompletedTurnFooter,
}: ProjectTurnTimelineProps) => {
  // This thread runs the same four-rung ladder as Evidence chat. The column
  // below is the turn-to-turn rung; the question-to-answer and within-answer
  // rungs are stated together on `AgentTimeline`, because that is the element
  // whose direct children are the question and the blocks of its answer.
  // Read the host's density with no local fallback. A fallback here is how the
  // thread and the prose inside it ended up on different densities on the same
  // surface — the blocks read the context default while this read `compact`.
  const density = useProseDensity();
  const turnRhythm = responseTurnClass(density);
  const answerRhythm = responseAnswerGapClass(density);

  const { leadingItems, turnGroups } = groupProjectTurns(items, isVisibleUserMessage);

  // The tail (thinking indicator, follow sentinel) is a continuation of the
  // latest answer, not a turn of its own — within-answer rhythm.
  const turnTail = (
    <div
      className={cn('flex min-w-0 flex-col', answerRhythm)}
      data-testid="project-chat-turn-tail"
    >
      {tailContent}
      <div ref={tailRef} data-testid="project-chat-follow-tail" aria-hidden="true" />
    </div>
  );

  if (turnGroups.length === 0) {
    return (
      <div className={cn('flex flex-col', responseColumnClass(density))}>
        <AgentTimeline
          items={items}
          renderItem={renderItem}
          rhythmClassName={turnRhythm}
        />
        {turnTail}
      </div>
    );
  }

  const completedTurns = turnGroups.slice(0, -1);
  const latestGroup = turnGroups[turnGroups.length - 1]!;

  return (
    <div className={cn('flex flex-col', responseColumnClass(density))}>
      {leadingItems.length > 0 ? (
        <AgentTimeline
          items={leadingItems}
          renderItem={renderItem}
          rhythmClassName={turnRhythm}
        />
      ) : null}
      {completedTurns.map((group, index) => (
        <div
          key={group.key}
          className={cn(
            'flex min-w-0 flex-col',
            answerRhythm,
            renderCompletedTurnFooter && RESPONSE_ACTIONS_HOVER_GROUP_CLASS_NAME
          )}
        >
          <AgentTimeline
            items={group.items}
            renderItem={renderItem}
            rhythmClassName={turnRhythm}
          />
          {renderCompletedTurnFooter?.({
            items: group.items,
            nextTurnFirstItem: turnGroups[index + 1]?.items[0],
          })}
        </div>
      ))}
      {/* The latest turn's own question sits inside its `AgentTimeline`, so
          this wrapper only separates that answer from its tail. */}
      <div
        className={cn('flex min-w-0 flex-col', answerRhythm)}
        data-testid="project-chat-latest-turn"
        style={{
          minHeight: latestTurnMinHeight ? `${latestTurnMinHeight}px` : undefined,
        }}
      >
        <div key={latestGroup.key}>
          <AgentTimeline
            items={latestGroup.items}
            renderItem={renderItem}
            rhythmClassName={turnRhythm}
          />
        </div>
        {turnTail}
      </div>
    </div>
  );
};
